import { isEmpty } from "radash";
import { useEffect } from "react";

import { viewTypes } from "@evolved/constants";
import { filterData, sortData } from "@evolved/views";

import { AddButton } from "../../components/add-button";
import { ContactDetailsModal } from "../../components/modals/contact-details-modal";
import { DownloadCSV } from "../../components/view/download-csv/download-csv";
import { Extras } from "./extras";
import { FieldSelect } from "../../components/view/fields-select";
import { Filters } from "../../components/view/filters/filters";
import { OpenImportModal } from "../../components/modals/import-data";
import { SaveView } from "../../components/view/save-view";
import { SearchInput } from "../../components/view/search";
import { Sorters } from "../../components/view/sorters";
import { Table } from "../../components/view/table";
import { ViewActions } from "../../components/view/actions";
import { ViewSelect } from "../../components/view/view-select";
import {
  filterConfigKeys,
  reduceSearchResults,
  useSearch,
} from "../use-search";
import { useAccounts } from "../../data/use-accounts";
import { useContacts } from "../../data/use-contacts";
import { useDefaultView } from "../use-default-view";
import { useFieldConfigs } from "../../components/view/use-field-configs";
import { useModal } from "../../hooks/use-modal";
import { useOpportunities } from "../../data/use-opportunities";
import { useSearchStore } from "../../stores/search";
import { useTags } from "../../data/use-tags";
import { useVendors } from "../../data/use-vendors";
import { useViewStore } from "../../stores/view";

const CreateContactButton = () => {
  const { modal, open } = useModal(ContactDetailsModal);

  return (
    <>
      {modal}
      <AddButton id="create-contact-button" onClick={open}>
        Contact
      </AddButton>
    </>
  );
};

export const ContactsRoute = () => {
  const accounts = useAccounts();
  const contacts = useContacts();
  const opportunities = useOpportunities();
  const vendors = useVendors();
  const tags = useTags();

  const includeArchived = useViewStore(
    (state) => state[viewTypes.CONTACT].includeArchived
  );

  useDefaultView(viewTypes.CONTACT);

  const fieldConfigs = useFieldConfigs(viewTypes.CONTACT);

  const search = useSearch(
    {
      primary: {
        items: contacts.all({ includeArchived }),
        keys: filterConfigKeys(fieldConfigs),
      },
      secondary: [
        { path: "accountId", items: accounts.all(), keys: ["name"] },
        { path: "opportunities", items: opportunities.all(), keys: ["alias"] },
        { path: "tags", items: tags.all(), keys: ["label"] },
        { path: "vendorId", items: vendors.all(), keys: ["name"] },
      ],
    },
    viewTypes.CONTACT
  );

  useEffect(() => {
    if (useSearchStore.getState()[viewTypes.CONTACT].query) {
      search(useSearchStore.getState()[viewTypes.CONTACT].query);
    }
  }, [includeArchived, accounts, contacts, opportunities, tags, vendors]);

  const searchResult = useSearchStore(
    (state) => state[viewTypes.CONTACT].result
  );

  const filters = useViewStore((state) => state[viewTypes.CONTACT].filters);
  const sortOrder = useViewStore((state) => state[viewTypes.CONTACT].sortOrder);

  let data;

  if (searchResult) {
    data = reduceSearchResults(searchResult)(contacts);
  } else if (!isEmpty(filters)) {
    data = filterData({
      data: contacts.all({ includeArchived }),
      fieldConfigs,
      filters,
    });
  } else {
    data = contacts.all({ includeArchived });
  }

  if (!isEmpty(sortOrder)) {
    data = sortData({
      data,
      fieldConfigs,
      sortOrder,
    });
  }

  return (
    <div
      className="w-full"
      style={{
        display: "grid",
        gridTemplateColumns: "1fr",
        gridTemplateRows: "auto 1fr",
        height: "100%",
      }}
    >
      <div className="mb-4">
        <div className="mb-4 flex items-center">
          <ViewSelect viewType={viewTypes.CONTACT} />
          <FieldSelect viewType={viewTypes.CONTACT} />
          <ViewActions viewType={viewTypes.CONTACT} />
          <SaveView viewType={viewTypes.CONTACT} />
          <div className="ml-auto flex">
            <Extras />
            <OpenImportModal type={viewTypes.CONTACT} />
            <div className="ml-2">
              <DownloadCSV data={data} viewType={viewTypes.CONTACT} />
            </div>
            <div className="ml-2">
              <CreateContactButton />
            </div>
          </div>
        </div>
        <div className="flex items-start">
          <div className="flex flex-wrap items-center">
            <div className="mr-4">
              <SearchInput search={search} viewType={viewTypes.CONTACT} />
            </div>
            <Filters viewType={viewTypes.CONTACT} />
            <Sorters viewType={viewTypes.CONTACT} />
          </div>
        </div>
      </div>
      <Table data={data} viewType={viewTypes.CONTACT} />
    </div>
  );
};
